import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { IntlShape, useIntl } from 'react-intl';

import { IndexApi, StatusCollectionEnum } from '../../../api/core';
import { getAuthMiddleware } from '../../../Auth/utils/GetAuthMiddleware';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import { InputGroup } from '../../edit/components/layout/InputGroup';

const POLL_INTERVAL_MS = 5000;

const COLLECTIONS: StatusCollectionEnum[] = ['Work', 'Person', 'Organisation', 'Project', 'Publisher'];

type IndexStatus = 'unknown' | 'IDLE' | 'BUSY' | 'ERROR' | string;

interface IndexState {
  status: IndexStatus;
  lastUpdate: Date | null;
  startedAt: Date | null;
}

type StateMap = Record<StatusCollectionEnum, IndexState>;

function createInitialState(): StateMap {
  const state = {} as StateMap;

  for (const c of COLLECTIONS) {
    state[c] = {
      status: 'unknown',
      lastUpdate: null,
      startedAt: null,
    };
  }

  return state;
}

export function AdminIndexing() {
  const { actions } = useContext(AuthContext);

  const api = useMemo(() => new IndexApi().withMiddleware(getAuthMiddleware(actions)), [actions]);

  const [states, setStates] = useState<StateMap>(createInitialState);

  const anyBusy = useMemo(() => Object.values(states).some((s) => s.status === 'BUSY'), [states]);

  const loadStatuses = useCallback(async () => {
    const results = await Promise.all(
      COLLECTIONS.map(async (collection) => {
        try {
          const status = await api.status({ collection });
          return { collection, status };
        } catch {
          return { collection, status: 'ERROR' as const };
        }
      })
    );

    setStates((prev) => {
      const next = { ...prev };

      for (const r of results) {
        const prevState = prev[r.collection];
        const newStatus = r.status;

        const becameIdle = newStatus === 'IDLE' && prevState.status === 'BUSY';

        next[r.collection] = {
          ...prevState,
          status: newStatus,
          lastUpdate: becameIdle ? new Date() : prevState.lastUpdate,
        };
      }

      return next;
    });
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | undefined;

    const poll = async () => {
      if (cancelled) return;

      await loadStatuses();

      if (!cancelled) {
        timeoutId = window.setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [loadStatuses]);

  const update = useCallback(
    async (collection: StatusCollectionEnum) => {
      setStates((prev) => ({
        ...prev,
        [collection]: {
          ...prev[collection],
          status: 'BUSY',
          startedAt: new Date(),
        },
      }));

      try {
        await api.indexCollection({ collection });
      } catch (err) {
        setStates((prev) => ({
          ...prev,
          [collection]: {
            ...prev[collection],
            status: 'ERROR',
          },
        }));

        console.error(`Failed to start indexing for ${collection}`, err);
      }
    },
    [api]
  );

  return (
    <div className="flex flex-column gap-2">
      {COLLECTIONS.map((collection) => (
        <AdminIndex
          key={collection}
          collection={collection}
          state={states[collection]}
          disabled={anyBusy}
          onUpdate={update}
        />
      ))}
    </div>
  );
}

interface AdminIndexProps {
  collection: StatusCollectionEnum;
  state: IndexState;
  disabled: boolean;
  onUpdate: (c: StatusCollectionEnum) => Promise<void>;
}

const AdminIndex = React.memo(function AdminIndex({ collection, state, disabled, onUpdate }: AdminIndexProps) {
  const isBusy = state.status === 'BUSY';

  return (
    <div className="grid align-items-center w-full">
      {/* Collection label */}
      <div className="col-6 flex-order-1 md:col-3">
        <label className="font-medium">{collection}</label>
      </div>

      {/* Status + timestamp */}
      <div className="col-12 flex-order-3 md:col-7 md:flex-order-2">
        <div className="p-inputtext flex justify-content-between align-items-center w-full">
          <span>{state.status}</span>

          <span className="text-500 text-sm white-space-nowrap">
            {state.lastUpdate
              ? state.lastUpdate.toLocaleString(undefined, {
                  dateStyle: 'short',
                  timeStyle: 'medium',
                })
              : ''}
          </span>
        </div>
      </div>

      {/* Action button */}
      <div className="col-6 flex-order-2 md:col-2 md:flex-order-3 flex justify-content-end md:justify-content-start">
        <Button
          label="Start"
          icon="pi pi-refresh"
          loading={isBusy}
          disabled={disabled || isBusy}
          onClick={() => void onUpdate(collection)}
        />
      </div>
    </div>
  );
});
